import { useState, useEffect, useCallback } from 'react';
import Layout from '../components/Layout';
import { getExceptionQueue, resolveException, linkDocument } from '../services/api';
import { toast } from 'sonner';
import { Button } from '../components/ui/button';
import { DollarSign, User, Landmark, FileWarning, UserX, FileText, TrendingDown, Filter, CheckCircle2, XCircle, Paperclip, ChevronDown, ChevronUp } from 'lucide-react';

const CAT_META = {
  capitalization: { icon: DollarSign, color: '#D98A4A', label: 'Capitalization' },
  owner_related: { icon: User, color: '#C84B31', label: 'Owner-Related' },
  loan_related: { icon: Landmark, color: '#D98A4A', label: 'Loan-Related' },
  tax_sensitive: { icon: FileWarning, color: '#C84B31', label: 'Tax-Sensitive' },
  first_time_vendor: { icon: UserX, color: '#4B5563', label: 'New Vendor' },
  missing_documentation: { icon: FileText, color: '#9CA3AF', label: 'Missing Docs' },
  unusual_pattern: { icon: TrendingDown, color: '#1A1A1A', label: 'Unusual' },
};

export default function ExceptionQueuePage() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [activeFilter, setActiveFilter] = useState(null);
  const [expandedId, setExpandedId] = useState(null);

  const fetchData = useCallback(async () => {
    try {
      const res = await getExceptionQueue(activeFilter);
      setData(res.data);
    } catch { toast.error('Failed to load exceptions'); }
    setLoading(false);
  }, [activeFilter]);

  useEffect(() => { fetchData(); }, [fetchData]);

  const handleResolve = async (txnId, resolution) => {
    try {
      await resolveException(txnId, { resolution });
      toast.success(`Transaction ${resolution}`);
      fetchData();
    } catch { toast.error('Failed to resolve'); }
  };

  const handleLinkDoc = async (txnId) => {
    try {
      await linkDocument(txnId, { document_ref: 'manual_link' });
      toast.success('Document linked');
      fetchData();
    } catch { toast.error('Failed to link document'); }
  };

  if (loading) return <Layout title="Exception Queue"><div className="flex items-center justify-center h-64"><div className="w-8 h-8 border-2 border-t-transparent rounded-full animate-spin" style={{ borderColor: '#D98A4A', borderTopColor: 'transparent' }}></div></div></Layout>;

  const { exceptions = [], total = 0, category_counts = {} } = data || {};

  return (
    <Layout title="Exception Queue">
      <div data-testid="exception-queue-page" className="space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <p className="font-body text-sm" style={{ color: '#4B5563' }}>
              <strong style={{ color: '#1A1A1A' }}>{total}</strong> transactions requiring policy review
            </p>
          </div>
        </div>

        {/* Category Filters */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => { setActiveFilter(null); setLoading(true); }}
            className="px-3.5 py-1.5 rounded-full text-xs font-body font-medium transition-all"
            style={{
              background: !activeFilter ? '#1A1A1A' : '#FFFFFF',
              color: !activeFilter ? '#FFFFFF' : '#4B5563',
              border: '1px solid #E5E3DB',
            }}
            data-testid="filter-all"
          >
            All ({total})
          </button>
          {Object.entries(CAT_META).map(([key, meta]) => {
            const count = category_counts[key] || 0;
            if (count === 0 && key !== activeFilter) return null;
            const Icon = meta.icon;
            return (
              <button
                key={key}
                onClick={() => { setActiveFilter(activeFilter === key ? null : key); setLoading(true); }}
                className="px-3.5 py-1.5 rounded-full text-xs font-body font-medium transition-all flex items-center gap-1.5"
                style={{
                  background: activeFilter === key ? meta.color : '#FFFFFF',
                  color: activeFilter === key ? '#FFFFFF' : meta.color,
                  border: `1px solid ${activeFilter === key ? meta.color : '#E5E3DB'}`,
                }}
                data-testid={`filter-${key}`}
              >
                <Icon size={12} /> {meta.label} ({count})
              </button>
            );
          })}
        </div>

        {/* Exception List */}
        {exceptions.length > 0 ? (
          <div className="space-y-3">
            {exceptions.map((exc) => {
              const isExpanded = expandedId === exc.transaction_id;
              return (
                <div key={exc.transaction_id} className="bg-white rounded-xl overflow-hidden transition-all" style={{ border: '1px solid #E5E3DB' }} data-testid={`exception-${exc.transaction_id}`}>
                  {/* Row */}
                  <div className="px-5 py-4 flex items-center gap-4 cursor-pointer" onClick={() => setExpandedId(isExpanded ? null : exc.transaction_id)}>
                    <div className="flex gap-1 flex-shrink-0">
                      {exc.categories.slice(0, 3).map((cat) => {
                        const m = CAT_META[cat];
                        if (!m) return null;
                        const CatIcon = m.icon;
                        return <div key={cat} className="w-7 h-7 rounded-lg flex items-center justify-center" style={{ background: `${m.color}15` }} title={m.label}><CatIcon size={14} style={{ color: m.color }} /></div>;
                      })}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2">
                        <span className="text-sm font-body font-medium truncate" style={{ color: '#1A1A1A' }}>{exc.vendor_name || 'Unknown'}</span>
                        <span className="font-mono text-xs" style={{ color: '#9CA3AF' }}>{exc.date}</span>
                      </div>
                      <p className="text-xs font-body truncate" style={{ color: '#4B5563' }}>{exc.description}</p>
                    </div>
                    <div className="text-right flex-shrink-0">
                      <p className="font-mono text-sm font-medium" style={{ color: '#1A1A1A' }}>${Math.abs(exc.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })}</p>
                      <div className="flex items-center gap-1 justify-end">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ background: exc.confidence < 40 ? '#C84B31' : exc.confidence < 70 ? '#D98A4A' : '#2D7A5D' }}></div>
                        <span className="font-mono text-[10px]" style={{ color: '#9CA3AF' }}>{exc.confidence}%</span>
                      </div>
                    </div>
                    <div className="flex items-center gap-2 flex-shrink-0">
                      {!exc.document_linked && (
                        <button onClick={(e) => { e.stopPropagation(); handleLinkDoc(exc.transaction_id); }} className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(161,161,165,0.1)' }} title="Link document" data-testid={`link-doc-${exc.transaction_id}`}>
                          <Paperclip size={14} style={{ color: '#9CA3AF' }} />
                        </button>
                      )}
                      <button onClick={(e) => { e.stopPropagation(); handleResolve(exc.transaction_id, 'approved'); }} className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(45,106,79,0.1)' }} title="Approve" data-testid={`approve-${exc.transaction_id}`}>
                        <CheckCircle2 size={14} style={{ color: '#2D7A5D' }} />
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleResolve(exc.transaction_id, 'rejected'); }} className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ background: 'rgba(157,2,8,0.1)' }} title="Reject" data-testid={`reject-${exc.transaction_id}`}>
                        <XCircle size={14} style={{ color: '#C84B31' }} />
                      </button>
                      {isExpanded ? <ChevronUp size={16} style={{ color: '#9CA3AF' }} /> : <ChevronDown size={16} style={{ color: '#9CA3AF' }} />}
                    </div>
                  </div>

                  {/* Expanded detail */}
                  {isExpanded && (
                    <div className="px-5 pb-4 pt-0 space-y-3" style={{ borderTop: '1px solid #E5E3DB' }}>
                      <div className="grid grid-cols-1 md:grid-cols-3 gap-3 pt-3">
                        {exc.pattern_summary && (
                          <div className="p-3 rounded-lg" style={{ background: 'rgba(224,122,95,0.04)', border: '1px solid rgba(224,122,95,0.15)' }}>
                            <p className="text-[9px] font-body uppercase tracking-wider font-semibold mb-1" style={{ color: '#C84B31' }}>Pattern Evidence</p>
                            <p className="text-xs font-body" style={{ color: '#4B5563' }}>{exc.pattern_summary}</p>
                          </div>
                        )}
                        {exc.policy_check && (
                          <div className="p-3 rounded-lg" style={{ background: 'rgba(198,156,109,0.04)', border: '1px solid rgba(198,156,109,0.15)' }}>
                            <p className="text-[9px] font-body uppercase tracking-wider font-semibold mb-1" style={{ color: '#D98A4A' }}>Policy Check</p>
                            <p className="text-xs font-body" style={{ color: '#4B5563' }}>{exc.policy_check}</p>
                          </div>
                        )}
                        {exc.review_reason && (
                          <div className="p-3 rounded-lg" style={{ background: 'rgba(244,162,97,0.04)', border: '1px solid rgba(244,162,97,0.15)' }}>
                            <p className="text-[9px] font-body uppercase tracking-wider font-semibold mb-1" style={{ color: '#D98A4A' }}>Review Reason</p>
                            <p className="text-xs font-body" style={{ color: '#4B5563' }}>{exc.review_reason}</p>
                          </div>
                        )}
                      </div>
                      <div className="flex flex-wrap gap-1.5">
                        {exc.category_labels.map((label) => (
                          <span key={label} className="px-2 py-0.5 rounded-full text-[10px] font-body" style={{ background: 'rgba(28,28,30,0.06)', color: '#4B5563' }}>{label}</span>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        ) : (
          <div className="bg-white rounded-xl p-16 text-center" style={{ border: '1px solid #E5E3DB' }}>
            <CheckCircle2 size={40} className="mx-auto mb-4" style={{ color: '#2D7A5D' }} />
            <h3 className="font-heading text-xl font-medium mb-2" style={{ color: '#1A1A1A' }}>All Clear</h3>
            <p className="text-sm font-body" style={{ color: '#9CA3AF' }}>No policy exceptions require attention</p>
          </div>
        )}
      </div>
    </Layout>
  );
}
